import type { EventIdType } from 'amplitude/amplitude'

/** Full-page blocked checkout UI: `error-code` → default user-facing copy (i18n keys). */
export const OPTIMIZED_CHECKOUT_ERRORS = {
  CHECKOUT_DISABLED: 'Checkout is disabled for this organization. Please go back to proposal page for the next steps.',
  CHECKOUT_UNAVAILABLE:
    'Optimized checkout is not available for this organization. Please go back to proposal page for the next steps.',
  PROJECT_SOLD_TO_DIFFERENT_SYSTEM_PAYMENT_OPTION:
    'This project is already marked as sold on a different system or payment option. Please go back to proposal page for the next steps.',
  NOT_FOUND: 'This link seems broken. Please go back to proposal page for the next steps.',
  INVALID_SESSION:
    'The online proposal you are trying to access is not valid. Please check the URL or reach out to your solar installer to request a new online proposal link.',
  PROPOSAL_EXPIRED: 'This proposal is no longer valid. Please go back to proposal page for the next steps.',
} as const

export type OptimizedCheckoutErrorCode = keyof typeof OPTIMIZED_CHECKOUT_ERRORS

export const FEATURE_CONFIG_USE_OPTIMIZED_CHECKOUT_FOR_CASHFLOW_ORGS = 'use_optimized_checkout_for_cashflow_orgs'

/** Set false to enable optimized checkout for non-cashflow orgs */
export const OPTIMIZED_CHECKOUT_REQUIRES_ACTIVE_CASHFLOW_ORG = true

export type CheckoutStepIdentifier =
  | 'system_selection'
  | 'explore_finance'
  | 'get_financed'
  | 'contract_signing'
  | 'first_payment'

export type CheckoutStepChipStatus = 'available' | 'in_progress' | 'complete' | 'declined'

export type CheckoutChipColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default'

/** Mirrors backend `CheckoutStepChipStatus` → MUI Chip colour in timeline and finance status card. */
export const CHIP_COLOR_BY_STATUS: Record<CheckoutStepChipStatus, CheckoutChipColor> = {
  available: 'default',
  in_progress: 'primary',
  complete: 'success',
  declined: 'error',
}

export type CheckoutStep = {
  id: number
  title: string
  step: CheckoutStepIdentifier
  required?: boolean
  description?: string
  status?: 'pending' | 'in_progress' | 'complete'
  is_current?: boolean
  display_text?: string
  signed_date?: string
  chip_status?: CheckoutStepChipStatus
}

// template.json keys for each step identifier (translated at render time, e.g. HorizontalTimeline).
// Step set and order come from the `checkout/progress` API response.
export const CHECKOUT_STEP_TITLES: Record<CheckoutStepIdentifier, string> = {
  system_selection: 'Choose System',
  explore_finance: 'Explore Finance',
  get_financed: 'Get Financed',
  contract_signing: 'Sign Contract',
  first_payment: 'Make Payment',
}

// Steps that block the user from advancing until they are completed.
// `explore_finance` is intentionally excluded because cash flows treat it as
// optional (see `CashCheckoutStepsProgressBuilder.OPTIONAL_STEPS` in
// `django/osapi/project/checkout_v2/`).
export const REQUIRED_CHECKOUT_STEPS: CheckoutStepIdentifier[] = [
  'system_selection',
  'contract_signing',
  'first_payment',
]

/** Step completion events that receive `time_spent_seconds` and `total_checkout_duration_seconds`. */
export const CHECKOUT_V2_STEP_COMPLETION_EVENT_STEPS: Partial<Record<EventIdType, CheckoutStepIdentifier>> = {
  checkout_v2_finance_app_submitted: 'get_financed',
  checkout_v2_finance_step_skipped: 'explore_finance',
  checkout_v2_finance_marketplace_apply_now: 'explore_finance',
  checkout_v2_contract_signed: 'contract_signing',
  checkout_v2_deposit_payment_success: 'first_payment',
  checkout_v2_success_viewed: 'first_payment',
}
